/**
 * Roll up per-workspace bonus-progress payloads into one card payload.
 * Pay rules live per workspace; the Anticipated earnings card combines rosters
 * and must show EVERY workspace's commission/bonus lines in the breakdown.
 */
import {
  programFromConfig,
  sanitizeProgram,
  isLiveCommission,
  isLiveBonus,
  isLiveFlat,
} from "../components/SalesBonus/moneyRules";

const sumTotals = (pairs, key) =>
  pairs.reduce((s, [, r]) => s + (Number(r.totals?.[key]) || 0), 0);

/** Merge configs + programs so the breakdown lists each workspace's rules. */
export const mergeMultiWorkspaceBonus = (pairs, wsName = {}) => {
  const packs = pairs.map(([id, res]) => ({
    wsId: String(id),
    wsLabel: wsName[String(id)] || "Workspace",
    config: res.config || {},
    res,
  }));

  const kpiNames = {};
  packs.forEach((p) => Object.assign(kpiNames, p.config.kpiNames || {}));

  const withLadder = packs.find((p) => (p.config.milestones || []).length);
  const withMetric = packs.find((p) => p.config.revenueKpiId);
  const base = (withLadder || withMetric || packs[0]).res;

  if (packs.length === 1) {
    return {
      ...base,
      config: { ...packs[0].config, kpiNames },
    };
  }

  const commissions = [];
  const bonuses = [];
  const flats = [];
  let scoredOnKpiId = null;
  let milestones = [];

  packs.forEach(({ wsLabel, config }) => {
    const program = programFromConfig(config);
    if (program.scoredOnKpiId) {
      const n = (config.kpiNames || {})[program.scoredOnKpiId];
      if (n) kpiNames[program.scoredOnKpiId] = n;
    }
    if (!scoredOnKpiId && program.scoredOnKpiId) scoredOnKpiId = program.scoredOnKpiId;
    if (!milestones.length && program.milestones?.length) milestones = program.milestones;

    program.commissions.filter(isLiveCommission).forEach((c) => {
      commissions.push({
        ...c,
        label: c.label ? `${wsLabel} — ${c.label}` : `${wsLabel} — Commission`,
      });
    });
    program.bonuses.filter(isLiveBonus).forEach((b) => {
      bonuses.push({
        ...b,
        label: b.label ? `${wsLabel} — ${b.label}` : null,
      });
    });
    (program.flats || []).filter(isLiveFlat).forEach((f) => {
      flats.push({
        ...f,
        label: f.label ? `${wsLabel} — ${f.label}` : `${wsLabel} — Flat bonus`,
      });
    });
  });

  /* Combined ladder label: every workspace's scored-on metric name. */
  const ladderLabels = [];
  packs.forEach(({ config }) => {
    const program = programFromConfig(config);
    const id = program.scoredOnKpiId || config.revenueKpiId;
    const n = id ? (config.kpiNames || {})[id] : null;
    if (n && !ladderLabels.includes(n)) ladderLabels.push(n);
  });
  if (ladderLabels.length) {
    kpiNames.__ladderCombined = ladderLabels.join(" · ");
  }

  const program = sanitizeProgram({
    scoredOnKpiId,
    commissions,
    bonuses,
    flats,
    milestones,
  });

  const month = sumTotals(pairs, "month");
  const lastMonth = sumTotals(pairs, "lastMonth");

  return {
    ...base,
    config: {
      ...(withLadder?.config || withMetric?.config || packs[0].config),
      kpiNames,
      program,
      /* Keep legacy top-level keys in sync with the merged program so UI that
         still reads config.milestones / revenueKpiId does not show a stale
         workspace ladder ($0 Pays) beside a card-local program. */
      milestones: program.milestones || [],
      revenueKpiId: program.scoredOnKpiId
        || (withLadder?.config || withMetric?.config || packs[0].config).revenueKpiId
        || null,
    },
    totals: {
      commission: sumTotals(pairs, "commission"),
      bonuses: sumTotals(pairs, "bonuses"),
      ytd: sumTotals(pairs, "ytd"),
      month,
      lastMonth,
      monthDeltaPct:
        lastMonth > 0 ? Math.round(((month - lastMonth) / lastMonth) * 100) : null,
    },
    combinedWorkspaceNames: packs.map((p) => p.wsLabel).join(", "),
  };
};

export default mergeMultiWorkspaceBonus;
